import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { CountrySelect } from '@/components/CountrySelect';
import { templatesService, Template } from '@/services/templates.service';
import { getCountryCallingCode, CountryCode } from 'libphonenumber-js';
import { Info, MessageSquarePlus } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Start a conversation with someone who has never messaged us.
 *
 * Meta's 24-hour customer-service window means the FIRST message must be an
 * approved template — a free-form text would simply be rejected — so this
 * dialog is template-first by design, not by preference.
 */
export function NewChatDialog({
  open,
  onClose,
  onStarted,
  accountUid,
}: {
  open: boolean;
  onClose: () => void;
  onStarted: (phone: string) => void;
  accountUid?: string;
}) {
  const [country, setCountry] = useState<CountryCode>('AE');
  const [national, setNational] = useState('');
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [templateUid, setTemplateUid] = useState('');
  const [vars, setVars] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTemplates(null);
    templatesService.list({ status: 'APPROVED', limit: 100, accountUid })
      .then((r) => {
        setTemplates(r.items);
        setTemplateUid((cur) => cur || r.items[0]?.templateUid || '');
      })
      .catch(() => setTemplates([]));
  }, [open, accountUid]);

  const template = templates?.find((t) => t.templateUid === templateUid);

  // Placeholders the operator must fill before Meta will accept the send.
  const placeholders = useMemo(() => {
    const found = new Set<string>();
    const scan = (text?: string) => {
      (text ?? '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, k: string) => { found.add(k); return _m; });
    };
    scan(template?.body);
    scan(template?.headerText);
    return Array.from(found);
  }, [template]);

  const phone = `+${getCountryCallingCode(country)}${national.replace(/\D+/g, '').replace(/^0+/, '')}`;
  const phoneOk = national.replace(/\D+/g, '').length >= 6;
  const varsOk = placeholders.every((p) => (vars[p] ?? '').trim());

  const send = async () => {
    if (!templateUid) { toast.error('Pick an approved template.'); return; }
    setBusy(true);
    try {
      const r = await templatesService.startConversation(templateUid, {
        phone,
        variables: placeholders.length ? vars : undefined,
      });
      if (!r.ok) { toast.error(r.error ?? 'Send failed'); return; }
      toast.success('Message sent — conversation started');
      onStarted(phone);
      onClose();
      setNational('');
      setVars({});
    } catch {
      /* interceptor toasts */
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title="New conversation">
      <div className="space-y-4">
        <div className="flex items-start gap-2.5 rounded-lg border border-info/30 bg-info-soft/50 px-3 py-2.5">
          <Info size={14} className="mt-0.5 shrink-0 text-info" />
          <p className="text-xs leading-relaxed text-text-secondary">
            WhatsApp only allows an <strong>approved template</strong> as the first message to someone
            who hasn't written to you in the last 24 hours. Once they reply, you can send free-form
            messages for 24 hours.
          </p>
        </div>

        <FormField label="WhatsApp number" required>
          <div className="flex gap-2">
            <CountrySelect value={country} onChange={setCountry} />
            <Input
              value={national}
              onChange={(e) => setNational(e.target.value)}
              placeholder="50 123 4567"
              inputMode="tel"
              className="flex-1"
            />
          </div>
          {national && (
            <p className="mt-1 font-mono text-2xs text-text-muted">Sending to {phone}</p>
          )}
        </FormField>

        <FormField label="Template" required hintBelow hint="Only APPROVED templates can open a conversation.">
          {templates === null ? (
            <div className="skeleton h-9 rounded-lg" />
          ) : templates.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border bg-surface-alt/40 px-3 py-2.5 text-xs text-text-muted">
              No approved templates yet. Create and submit one in{' '}
              <a href="/templates" className="font-medium text-primary hover:underline">Templates</a>,
              then come back once Meta approves it.
            </p>
          ) : (
            <Select value={templateUid} onChange={(e) => { setTemplateUid(e.target.value); setVars({}); }}>
              {templates.map((t) => (
                <option key={t.templateUid} value={t.templateUid}>
                  {t.name} ({t.language}) · {t.category}
                </option>
              ))}
            </Select>
          )}
        </FormField>

        {template?.body && (
          <div className="rounded-lg border border-border bg-surface-alt/40 p-3">
            <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-text-muted">Preview</div>
            <p className="whitespace-pre-wrap text-xs text-text-secondary">{template.body}</p>
          </div>
        )}

        {placeholders.length > 0 && (
          <div className="space-y-2">
            <div className="text-2xs font-semibold uppercase tracking-wide text-text-muted">
              Template variables
            </div>
            {placeholders.map((p) => (
              <FormField key={p} label={p} required>
                <Input
                  value={vars[p] ?? ''}
                  onChange={(e) => setVars((s) => ({ ...s, [p]: e.target.value }))}
                  placeholder={`Value for {{${p}}}`}
                />
              </FormField>
            ))}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="primary"
            onClick={send}
            loading={busy}
            disabled={!phoneOk || !templateUid || !varsOk || busy}
            iconLeft={<MessageSquarePlus size={14} />}
          >
            Send &amp; open chat
          </Button>
        </div>
      </div>
    </Modal>
  );
}
